<script setup>
import { computed, ref, useTemplateRef } from 'vue'
import moment from 'moment'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import IconBase from '../icon/IconBase.vue'
import IconKebab from '../icon/IconKebab.vue'
import { getInviteTimestamp, teamRoleKey } from '@/Utils/teamAccessSummary'

const RESEND_COOLDOWN_MS = 60 * 1000

const props = defineProps({
	member: { type: Object, required: true },
	// Disable the pause/enable action while a status change is in-flight.
	permissionTogglestatus: { type: Boolean, default: false },
	// When viewing as a member (activeTeam), row mutations are backend-gated off.
	canMutate: { type: Boolean, default: true },
	// The workspace the page is standing in; names the per-project actions.
	activeProject: { type: Object, default: null },
	// Whether the account has more than one workspace. With one, "remove from
	// this workspace" and "remove from account" are the same thing, so only the
	// latter is offered.
	showProjects: { type: Boolean, default: false },
})

/**
 * `remove` carries `{ member, scope }`: `'project'` revokes this workspace's
 * grant (or withdraws this workspace's invitation); `'account'` removes the
 * person from every workspace. The confirmation lives in the page, in a dialog,
 * because what each one does to the person's seat needs a sentence, not a
 * menu item.
 */
const emit = defineEmits(['edit', 'add', 'resend', 'toggle', 'remove'])

const isPending = computed(() => props.member?.status === 'pending')
const isOwner = computed(() => teamRoleKey(props.member?.role) === 'owner')
const isPaused = computed(() => props.member?.enabled === false)
const notHere = computed(() => props.member?.onProject === false)
const projectName = computed(() => props.activeProject?.name || 'this workspace')
const projectCount = computed(() => (Array.isArray(props.member?.workspaces) ? props.member.workspaces.length : 0))

const invitedAgo = computed(() => {
	const at = getInviteTimestamp(props.member)
	return at ? `Invited ${moment(at).fromNow()}` : null
})

const cooldownUntil = ref(0)
const now = ref(Date.now())
const onCooldown = computed(() => now.value < cooldownUntil.value)

function tickNow() {
	now.value = Date.now()
}

const dropdownRef = useTemplateRef('dropdownRef')

const dangerClasses = 'h-10 md:h-8 !p-3 md:!p-2 hover:bg-surface-3 !text-error hover:!text-error text-nowrap leading-none text-base md:text-tiny flex items-center w-full'

function onMenuToggle() {
	tickNow()
}
function onEdit() {
	dropdownRef.value?.hide()
	emit('edit', props.member)
}
function onAdd() {
	dropdownRef.value?.hide()
	emit('add', props.member)
}
function onResend() {
	if (onCooldown.value) return
	cooldownUntil.value = Date.now() + RESEND_COOLDOWN_MS
	// keep `onCooldown` fresh so the item re-enables after the window
	const timer = setInterval(() => {
		tickNow()
		if (!onCooldown.value) clearInterval(timer)
	}, 1000)
	emit('resend', props.member)
}
function onToggle() {
	emit('toggle', props.member)
}
function onRemove(scope) {
	dropdownRef.value?.hide()
	emit('remove', { member: props.member, scope })
}
</script>

<template>
	<div v-if="!isOwner" class="flex justify-end">
		<DropDownMenu
			ref="dropdownRef"
			is-icon
			position="right"
			use-teleport
			:aria-label="`Actions for ${member.name}`"
			@toggle="onMenuToggle"
		>
			<template #toggle-button>
				<IconBase class="w-4 h-4">
					<IconKebab />
				</IconBase>
			</template>
			<template #menu-items>
				<!-- Pending invitation -->
				<template v-if="isPending">
					<template v-if="!notHere">
						<DropDownMenuItem
							:id="`edit-${member._id || member.id}`"
							:disabled="!canMutate"
							@click="onEdit"
						>
							Edit access
						</DropDownMenuItem>
						<DropDownMenuItem is-text v-if="invitedAgo">{{ invitedAgo }}</DropDownMenuItem>
						<DropDownMenuItem
							:aria-label="`Resend invitation to ${member.name}`"
							:disabled="!canMutate || onCooldown"
							@click="onResend"
						>
							{{ onCooldown ? 'Invitation sent, wait a moment' : 'Resend invitation' }}
						</DropDownMenuItem>
						<DropDownMenuItem
							:custom-classes="dangerClasses"
							:aria-label="`Cancel invitation for ${member.name}`"
							:disabled="!canMutate"
							@click="onRemove('project')"
						>
							{{ showProjects && projectCount > 1 ? `Cancel invitation to ${projectName}` : 'Cancel invitation' }}
						</DropDownMenuItem>
					</template>
					<DropDownMenuItem
						v-if="notHere || projectCount > 1"
						:custom-classes="dangerClasses"
						:aria-label="`Cancel all invitations for ${member.name}`"
						:disabled="!canMutate"
						@click="onRemove('account')"
					>
						{{ projectCount > 1 ? 'Cancel all invitations' : 'Cancel invitation' }}
					</DropDownMenuItem>
				</template>

				<!-- On the roster, but not on this workspace -->
				<template v-else-if="notHere">
					<DropDownMenuItem
						:aria-label="`Add ${member.name} to ${projectName}`"
						:disabled="!canMutate"
						@click="onAdd"
					>
						Add to {{ projectName }}
					</DropDownMenuItem>
					<DropDownMenuItem
						:custom-classes="dangerClasses"
						:aria-label="`Remove ${member.name} from account`"
						:disabled="!canMutate"
						@click="onRemove('account')"
					>
						Remove from account
					</DropDownMenuItem>
				</template>

				<!-- Active member of this workspace -->
				<template v-else>
					<DropDownMenuItem
						:id="`edit-${member._id || member.id}`"
						:disabled="!canMutate"
						@click="onEdit"
					>
						Edit access
					</DropDownMenuItem>
					<DropDownMenuItem
						:aria-label="`${isPaused ? 'Enable' : 'Pause'} ${member.name}`"
						:disabled="!canMutate || permissionTogglestatus"
						@click="onToggle"
					>
						<template v-if="showProjects">{{ isPaused ? 'Enable in' : 'Pause in' }} {{ projectName }}</template>
						<template v-else>{{ isPaused ? 'Enable member' : 'Pause member' }}</template>
					</DropDownMenuItem>

					<DropDownMenuItem
						v-if="showProjects"
						:custom-classes="dangerClasses"
						:aria-label="`Remove ${member.name} from ${projectName}`"
						:disabled="!canMutate"
						@click="onRemove('project')"
					>
						Remove from {{ projectName }}
					</DropDownMenuItem>
					<DropDownMenuItem
						:custom-classes="dangerClasses"
						:aria-label="`Remove ${member.name} from account`"
						:disabled="!canMutate"
						@click="onRemove('account')"
					>
						{{ showProjects ? 'Remove from account' : 'Remove member' }}
					</DropDownMenuItem>
				</template>
			</template>
		</DropDownMenu>
	</div>
</template>
